/**
 * sectionLavanderia.tsx — Vista RESUMEN ejecutivo de lavandería en /finanzas-empresa.
 *
 * Round 2026-05-12 (iter post-reembolso): rediseño "mostrar todas las
 * lavanderías apiladas". Antes el admin tenía que seleccionar una
 * lavandería del catálogo a la vez vía un Select en la parte superior;
 * ahora se renderiza una Card por cada lavandería del catálogo, todas
 * apiladas verticalmente. Cada Card mantiene su propio estado de fetch
 * (resumen + gastos op.) y su toggle individual "Ver más detalle".
 *
 * Round 2026-05-11 iter 23 (histórico): la sección antes mostraba la
 * tabla detallada de cada lavado con 12 columnas. Para el detalle
 * granular (filas, filtros, exportación) seguir yendo a /lavanderia —
 * esta vista es solo lectura ejecutiva.
 *
 * Cada Card por lavandería muestra:
 *   1. Tarjeta resumen del período: 3 KPIs (Estimado/Costo Real/Diferencia
 *      para genérica externa; Ingresos/Gastos/Utilidad para AF internas).
 *   2. Toggle "Ver más detalle" → KPIs adicionales (promedio por lavado,
 *      propiedades atendidas, lavados con extras) + desglose de gastos op.
 *   3. Botón "Ver registros completos en /lavanderia →" para drill-down
 *      al detalle granular.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Row, Spin, Statistic, Tag, Tooltip, Typography } from "antd";
import type { Dayjs } from "dayjs";
import {
  DownOutlined,
  InfoCircleOutlined,
  RightOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { getMsg } from "../../utils/antdStatic";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { FiltroFecha } from "./filtroGlobalFecha";
import { fetchAllPages } from "./exportXls";
import { ahoraChile } from "../../utils/fechaChile";

dayjs.locale("es");

// ── Types ──────────────────────────────────────────────────────────────────

export interface OpcionBasica {
  id: number;
  nombre: string;
}

interface LavanderiaOption {
  id: number;
  nombre: string;
  // `es_generica = true` identifica la "Lavandería General Externa"
  // (entrada catch-all del catálogo, no operada directamente por AF).
  es_generica?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const clp = (v: number | null | undefined) =>
  v != null
    ? new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
        maximumFractionDigits: 0,
      }).format(v)
    : "—";

/** Suma campo numérico tolerante a null/undefined/strings. */
const sumField = (rows: any[], field: string): number =>
  rows.reduce((acc, r) => acc + (Number(r?.[field]) || 0), 0);

/** Suma de los montos de un array JSONB tipo [{ ..., monto | total }]. */
const sumJsonb = (arr: any, key: "monto" | "total"): number =>
  Array.isArray(arr)
    ? arr.reduce((s, x) => s + (Number(x?.[key]) || 0), 0)
    : 0;

// ── Sub-componente: una Card por lavandería ────────────────────────────────
//
// Cada instancia tiene su propio fetch de resumen + gastos + KPIs lazy.
// Aislada en sub-componente para que el parent renderice N cards en
// paralelo sin pelearse por el state. Round 2026-05-12.

interface LavanderiaCardProps {
  lavanderia: LavanderiaOption;
  filtroFecha?: FiltroFecha;
  /** Contador de refresco manual (botón "Actualizar" del header). */
  refreshKey?: number;
}

function LavanderiaCard({
  lavanderia,
  filtroFecha,
  refreshKey,
}: LavanderiaCardProps) {
  const navigate = useNavigate();

  // Rango del período (vista MENSUAL fija) — heredado del filtro global.
  // Si el usuario eligió "rango", alineamos al mes inicial.
  const buildGlobalRange = useCallback(() => {
    const ff = filtroFecha ?? { modo: "mes", mes: ahoraChile() };
    let m: Dayjs;
    if (ff.modo === "rango" && ff.rango) {
      m = ff.rango[0];
    } else {
      m = ff.mes ?? ahoraChile();
    }
    return { from: m.startOf("month"), to: m.endOf("month") };
  }, [filtroFecha]);

  // Estado: resumen (totales agregados server-side) + gastos op. del período.
  const [resumen, setResumen] = useState<{
    cantidad: number;
    totalMonto: number;
    totalExtraMonto: number;
    totalGeneral: number;
    totalIngresoEstimado: number;
    totalCostoReal: number;
  }>({
    cantidad: 0,
    totalMonto: 0,
    totalExtraMonto: 0,
    totalGeneral: 0,
    totalIngresoEstimado: 0,
    totalCostoReal: 0,
  });
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [gastos, setGastos] = useState<any[]>([]);
  const [loadingGastos, setLoadingGastos] = useState(false);

  const fetchResumen = useCallback(async () => {
    try {
      setLoadingResumen(true);
      const gr = buildGlobalRange();
      const body = {
        filters: { fields: { lavanderia_id: { eq: lavanderia.id } } },
        globalDateRange: [
          gr.from.format("YYYY-MM-DD"),
          gr.to.format("YYYY-MM-DD"),
        ],
        page: 1,
        page_size: 1,
      };
      const json = await finanzasEmpresaApi.searchLavados(body);
      if (!json) return;
      setResumen({
        cantidad: Number(json.total ?? 0),
        totalMonto: Number(json.totalMonto ?? 0),
        totalExtraMonto: Number(json.totalExtraMonto ?? 0),
        totalGeneral: Number(json.totalGeneral ?? json.totalMonto ?? 0),
        totalIngresoEstimado: Number(json.totalIngresoEstimado ?? 0),
        totalCostoReal: Number(json.totalCostoReal ?? 0),
      });
    } catch (err: any) {
      getMsg()?.error(err?.message || `Error al cargar ${lavanderia.nombre}`);
    } finally {
      setLoadingResumen(false);
    }
  }, [lavanderia.id, lavanderia.nombre, buildGlobalRange]);

  const fetchGastos = useCallback(async () => {
    try {
      setLoadingGastos(true);
      const res = await finanzasEmpresaApi.getGastosOperacionales(lavanderia.id);
      const rows: any[] = res?.data ?? [];
      const gr = buildGlobalRange();
      // Se compara por MES en formato 'YYYY-MM', no por milisegundos.
      //
      // `lavanderia_af_gastos.periodo` es DATEONLY y marca un mes. Antes esto
      // hacía `dayjs(r.periodo).valueOf()` —que parsea en la zona del
      // DISPOSITIVO— contra un borde construido con `ahoraChile()` —que está en
      // zona chilena—. Con el equipo fuera de Chile (o mal configurado en UTC)
      // las dos mitades quedaban desfasadas y el mes en curso caía fuera del
      // rango: la tarjeta mostraba «Sin gastos operacionales» y una utilidad de
      // sub-entidad inflada, porque los gastos no se restaban. Comparar los
      // 'YYYY-MM' como strings es exacto y no depende de ninguna zona: el orden
      // lexicográfico coincide con el cronológico. Round 2026-08-18.
      const desdeMes = gr.from.format("YYYY-MM");
      const hastaMes = gr.to.format("YYYY-MM");
      const mesDe = (r: any) => String(r?.periodo ?? "").slice(0, 7);
      const inRange = rows.filter((r) => {
        const m = mesDe(r);
        return m.length === 7 && m >= desdeMes && m <= hastaMes;
      });
      inRange.sort((a, b) => mesDe(b).localeCompare(mesDe(a)));
      setGastos(inRange);
    } catch (err: any) {
      getMsg()?.error(
        err?.message || `Error al cargar gastos op. de ${lavanderia.nombre}`,
      );
    } finally {
      setLoadingGastos(false);
    }
  }, [lavanderia.id, lavanderia.nombre, buildGlobalRange]);

  useEffect(() => {
    fetchResumen();
    fetchGastos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchResumen, fetchGastos, refreshKey]);

  // Totales derivados.
  const totalGastosOp = useMemo(() => sumField(gastos, "total_gastos"), [gastos]);

  // Dos modos de cálculo según es_generica.
  //
  // Round 2026-05-12 — Cleanup post-auditoría (issue E): la fórmula
  // previa `costoReal = totalCostoReal + totalGastosOp` para !esGenerica
  // era DEAD CODE (`costoReal` y `diferenciaExterna` solo se renderizan
  // dentro del branch esGenerica) Y conceptualmente errónea: mezclaba
  // ingresos extra de la sub-entidad (lavados manuales rol='paga'
  // apuntando a una AF interna — modelan "AF Empresa transfiere algo
  // adicional a la AF interna") con gastos op. propios. Ahora `costoReal`
  // significa lo mismo en ambos modos: SUM(monto+extra) WHERE rol='paga'.
  // Solo se RENDERIZA dentro del branch esGenerica (para Estimado vs Real);
  // para AF interna típicamente costoReal=0 salvo ajustes manuales raros.
  //
  // Divergencia documentada entre vistas: el cron oficial
  // (`resumenesCron.js` + `estadisticas.ingresos_lavanderia_af_propia`)
  // calcula "Ingresos AF interna" como SUM(monto+extra) WHERE rol IN
  // ('recibe','intermediario'), es decir `totalIngresoEstimado`. La vista
  // de Card acá usa `totalGeneral` que ADEMÁS incluye lavados manuales
  // rol='paga' apuntando a la AF interna (esos lavados modelan "AF
  // Empresa transfiere algo extra a la AF interna" → ingreso real para
  // la sub-entidad). En la práctica son raros y la divergencia es 0;
  // si en el futuro un admin los usa con frecuencia, la cifra de la Card
  // será MAYOR que la del cron. Decisión consciente: la Card refleja el
  // flujo de caja real de la sub-entidad (todo lo que le entró);
  // los reportes oficiales usan el subset estandarizado. Ver
  // `flujo-financiero.md §6.5` para una nota análoga aplicada a aseos.
  const esGenerica = lavanderia.es_generica === true;
  const ingresoEstimado = resumen.totalIngresoEstimado;
  const costoReal = resumen.totalCostoReal;
  const diferenciaExterna = ingresoEstimado - costoReal;
  const utilidadAFInterna = resumen.totalGeneral - totalGastosOp;

  // KPIs avanzados — lazy: solo cuando el usuario expande detalle.
  type KpisAFInterna = {
    kind: "af";
    propiedadesAtendidas: number;
    lavadosConExtras: number;
    promedioPorLavado: number;
  };
  type KpisGenerica = {
    kind: "generica";
    estimados: {
      count: number;
      promedio: number;
      propiedades: number;
    };
    reales: {
      count: number;
      promedio: number;
    };
    // Round 2026-07-21: pass-through neto 0 (rol='intermediario'). Bucket
    // aparte — NO se mezcla con estimado ni con costo real.
    intermediados: {
      count: number;
      total: number;
    };
  };
  const [expanded, setExpanded] = useState(false);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpis, setKpis] = useState<KpisAFInterna | KpisGenerica | null>(null);

  useEffect(() => {
    // Reset KPIs al cambiar de período (la lavandería es prop fija por Card).
    setKpis(null);
    setExpanded(false);
  }, [filtroFecha]);

  const loadKpis = useCallback(async () => {
    if (kpis != null) return;
    try {
      setKpisLoading(true);
      const gr = buildGlobalRange();
      const baseBody = {
        filters: { fields: { lavanderia_id: { eq: lavanderia.id } } },
        globalDateRange: [
          gr.from.format("YYYY-MM-DD"),
          gr.to.format("YYYY-MM-DD"),
        ],
      };
      const allRows = await fetchAllPages<any>(async (page, pageSize) => {
        const body = { ...baseBody, page, page_size: pageSize };
        const json = await finanzasEmpresaApi.searchLavados(body);
        if (!json) return null;
        return { data: json.data, total: json.total };
      });

      if (esGenerica) {
        // Round 2026-07-21: en la genérica, 'intermediario' es pass-through
        // neto 0 (NO es presupuesto estimado). Se filtra a su propio bucket
        // para no inflar el Estimado ni la Diferencia AF (que serían fantasma).
        // Ver flujo-financiero.md §7.3.
        const estimadosRows = allRows.filter(
          (r) => r?.rolDeAlojaFacil === "recibe",
        );
        const realesRows = allRows.filter(
          (r) => r?.rolDeAlojaFacil === "paga",
        );
        const intermediadosRows = allRows.filter(
          (r) => r?.rolDeAlojaFacil === "intermediario",
        );
        const sumMontoYExtra = (rows: any[]) =>
          rows.reduce(
            (s, r) =>
              s + (Number(r?.monto) || 0) + (Number(r?.extraMonto) || 0),
            0,
          );
        const totalEstimado = sumMontoYExtra(estimadosRows);
        const totalReal = sumMontoYExtra(realesRows);
        const totalIntermediado = sumMontoYExtra(intermediadosRows);
        const propiedadesUnicasEst = new Set(
          estimadosRows.map((r) => r?.propiedadId).filter((x) => x != null),
        );
        setKpis({
          kind: "generica",
          estimados: {
            count: estimadosRows.length,
            promedio:
              estimadosRows.length > 0
                ? Math.round(totalEstimado / estimadosRows.length)
                : 0,
            propiedades: propiedadesUnicasEst.size,
          },
          reales: {
            count: realesRows.length,
            promedio:
              realesRows.length > 0
                ? Math.round(totalReal / realesRows.length)
                : 0,
          },
          intermediados: {
            count: intermediadosRows.length,
            total: totalIntermediado,
          },
        });
        return;
      }

      const propiedadesUnicas = new Set(
        allRows.map((r) => r?.propiedadId).filter((x) => x != null),
      );
      const conExtras = allRows.filter((r) => Number(r?.extraMonto) > 0).length;
      const promedio =
        allRows.length > 0
          ? allRows.reduce(
              (s, r) =>
                s + (Number(r?.monto) || 0) + (Number(r?.extraMonto) || 0),
              0,
            ) / allRows.length
          : 0;
      setKpis({
        kind: "af",
        propiedadesAtendidas: propiedadesUnicas.size,
        lavadosConExtras: conExtras,
        promedioPorLavado: Math.round(promedio),
      });
    } catch (err: any) {
      getMsg()?.error(err?.message || "Error al cargar KPIs");
    } finally {
      setKpisLoading(false);
    }
  }, [lavanderia.id, kpis, buildGlobalRange, esGenerica]);

  const handleToggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadKpis();
  };

  // Deep-link a /lavanderia correlacionado con lo que muestra ESTA card:
  // preselecciona esta lavandería (?lavanderiaId=) y el MISMO mes del período
  // activo (?mes=YYYY-MM). Así /lavanderia abre su tabla granular filtrada 1:1
  // con las cifras del resumen — el mismo drill-down que el "Ver detalle" de
  // la pestaña Aseos. finanzas-empresa es mensual, así que buildGlobalRange
  // siempre devuelve un mes completo (from = inicio de mes).
  const verDetalleEnLavanderia = useCallback(() => {
    const gr = buildGlobalRange();
    const params = new URLSearchParams();
    params.set("lavanderiaId", String(lavanderia.id));
    params.set("mes", gr.from.format("YYYY-MM"));
    navigate(`/lavanderia?${params.toString()}`);
  }, [buildGlobalRange, lavanderia.id, navigate]);

  // Desglose agregado de gastos op. del período.
  const gastosDesglose = useMemo(() => {
    const servicios = gastos.reduce(
      (s, r) =>
        s +
        (Number(r?.agua) || 0) +
        (Number(r?.gas) || 0) +
        (Number(r?.arriendo) || 0) +
        (Number(r?.luz) || 0),
      0,
    );
    const sueldos = gastos.reduce(
      (s, r) => s + sumJsonb(r?.sueldos, "monto"),
      0,
    );
    const materiales = gastos.reduce(
      (s, r) => s + sumJsonb(r?.materiales, "total"),
      0,
    );
    return { servicios, sueldos, materiales };
  }, [gastos]);

  return (
    <Card
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "20px 24px" }}
      loading={loadingResumen || loadingGastos}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag
            color={esGenerica ? "default" : "blue"}
            style={{ fontSize: 13, marginRight: 0 }}
          >
            {lavanderia.nombre}
            {esGenerica ? " · Sistema" : ""}
          </Tag>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {esGenerica
              ? "Externa — Estimado vs Real"
              : "AF interna — sub-entidad con utilidad propia"}
          </span>
        </div>
      }
    >
      <Row gutter={[24, 16]} align="middle">
        {esGenerica ? (
          <>
            {/* GENÉRICA EXTERNA: Estimado vs Real */}
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Presupuesto reservado: lo que AF cobró al huésped por el subitem lavandería de cada booking (lavados auto rol='recibe').">
                    <span style={{ fontSize: 13, color: "#1677ff", fontWeight: 600 }}>
                      💡 Estimado (presupuesto) <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={ingresoEstimado}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{ color: "#1677ff", fontSize: 22 }}
                suffix={
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 400 }}>
                    ({resumen.cantidad} {resumen.cantidad === 1 ? "lavado" : "lavados"})
                  </span>
                }
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Costo real anotado a mano: lavados manuales con 'Pagado a esta lavandería' (rol='paga'), agregados en /lavanderia → Costo real. La genérica externa NO usa gastos operacionales (esos son solo para AF internas).">
                    <span style={{ fontSize: 13, color: "#cf1322", fontWeight: 600 }}>
                      💸 Costo real (manual) <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={costoReal}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{ color: "#cf1322", fontSize: 22 }}
                suffix={
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 400 }}>
                    (lavados manuales rol='paga')
                  </span>
                }
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Diferencia Estimado − Real. Positivo = AF presupuestó MÁS de lo que costó (ganancia para AF). Negativo = costó más de lo presupuestado (pérdida real para AF).">
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      📊 Diferencia AF <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={diferenciaExterna}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{
                  color: diferenciaExterna >= 0 ? "#389e0d" : "#cf1322",
                  fontSize: 26,
                  fontWeight: 700,
                }}
                prefix={diferenciaExterna >= 0 ? "+" : ""}
              />
            </Col>
          </>
        ) : (
          <>
            {/* AF INTERNA: Ingresos − gastos op. = Utilidad sub-entidad */}
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip
                    title={
                      "Ingresos brutos de la sub-entidad — SUMA de monto + extra " +
                      "de TODOS los lavados de esta lavandería en el período " +
                      "(AUTO intermediario desde booking + manuales rol='paga'/" +
                      "'recibe' apuntando a esta AF). NOTA: el cron oficial " +
                      "(estadisticas.ingresos_lavanderia_af_propia) usa SOLO " +
                      "rol IN ('recibe','intermediario'); en la práctica las " +
                      "dos cifras coinciden salvo que existan ajustes manuales " +
                      "rol='paga' a esta AF (caso raro)."
                    }
                  >
                    <span style={{ fontSize: 13, color: "#52c41a", fontWeight: 600 }}>
                      Ingresos sub-entidad <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={resumen.totalGeneral}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{ color: "#389e0d", fontSize: 22 }}
                suffix={
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 400 }}>
                    ({resumen.cantidad} {resumen.cantidad === 1 ? "lavado" : "lavados"})
                  </span>
                }
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Gastos operacionales propios de la sub-entidad: servicios (agua/gas/luz/arriendo) + sueldos + materiales. NO afectan la utilidad de AF Empresa — son de la sub-entidad.">
                    <span style={{ fontSize: 13, color: "#cf1322", fontWeight: 600 }}>
                      Gastos op. propios <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={totalGastosOp}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{ color: "#cf1322", fontSize: 22 }}
                suffix={
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 400 }}>
                    ({gastos.length} {gastos.length === 1 ? "mes" : "meses"})
                  </span>
                }
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={
                  <Tooltip title="Utilidad de esta sub-entidad AF (ingresos − gastos op.). Es del grupo AF pero contabilizada aparte de AF Empresa.">
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      Utilidad sub-entidad <InfoCircleOutlined />
                    </span>
                  </Tooltip>
                }
                value={utilidadAFInterna}
                precision={0}
                formatter={(v) => clp(Number(v))}
                valueStyle={{
                  color: utilidadAFInterna >= 0 ? "#389e0d" : "#cf1322",
                  fontSize: 26,
                  fontWeight: 700,
                }}
                prefix={utilidadAFInterna >= 0 ? "+" : ""}
              />
            </Col>
          </>
        )}
      </Row>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        {/* Drill-down directo a /lavanderia (esta lavandería + mismo mes),
            SIN tener que expandir primero. Ver detalle granular filtrado 1:1
            con lo que muestra la card. */}
        <Tooltip title="Abrir /lavanderia con esta lavandería y este mes ya filtrados">
          <Button
            type="link"
            icon={<ArrowRightOutlined />}
            onClick={verDetalleEnLavanderia}
          >
            Ver detalle en /lavanderia
          </Button>
        </Tooltip>
        <Button
          type="link"
          onClick={handleToggleExpanded}
          icon={expanded ? <DownOutlined /> : <RightOutlined />}
        >
          {expanded ? "Ocultar detalle" : "Ver más detalle"}
        </Button>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            background: "#fafafa",
            borderRadius: 6,
          }}
        >
          {kpisLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
              <div style={{ marginTop: 12, color: "#888" }}>
                Calculando KPIs adicionales...
              </div>
            </div>
          ) : esGenerica && kpis?.kind === "generica" ? (
            <>
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Typography.Title level={5} style={{ marginTop: 0 }}>
                    💡 Presupuesto estimado — métricas
                  </Typography.Title>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Statistic
                        title="Cantidad de lavados"
                        value={kpis.estimados.count}
                        valueStyle={{ fontSize: 16 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Promedio por lavado"
                        value={kpis.estimados.promedio}
                        formatter={(v) => clp(Number(v))}
                        valueStyle={{ fontSize: 16 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Propiedades con presupuesto"
                        value={kpis.estimados.propiedades}
                        valueStyle={{ fontSize: 16 }}
                      />
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Typography.Title level={5} style={{ marginTop: 0 }}>
                    💸 Costo real — métricas
                  </Typography.Title>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Statistic
                        title="Costos reales anotados"
                        value={kpis.reales.count}
                        valueStyle={{ fontSize: 16 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Promedio por costo real"
                        value={kpis.reales.promedio}
                        formatter={(v) => clp(Number(v))}
                        valueStyle={{ fontSize: 16 }}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              {kpis.intermediados.count > 0 && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  message={`🔁 Intermediado (neto 0): ${clp(
                    kpis.intermediados.total,
                  )}`}
                  description={
                    <span>
                      {kpis.intermediados.count}{" "}
                      {kpis.intermediados.count === 1
                        ? "lavado intermediado"
                        : "lavados intermediados"}{" "}
                      este período: AF le cobró ese monto a alguien y le pagó lo
                      mismo al proveedor externo. Neto AF = 0: no afecta el
                      presupuesto estimado, el costo real ni la Diferencia AF.
                    </span>
                  }
                />
              )}
              {kpis.estimados.count > 0 && kpis.reales.count === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                  message={`${kpis.estimados.count} ${
                    kpis.estimados.count === 1
                      ? "lavado presupuestado sin costo real anotado"
                      : "lavados presupuestados sin costos reales anotados"
                  }`}
                  description={
                    <span>
                      Para auditar el presupuesto, anota lo que pagaste al
                      proveedor externo en{" "}
                      <a
                        onClick={() => navigate("/lavanderia")}
                        style={{ cursor: "pointer" }}
                      >
                        /lavanderia → Costo real
                      </a>
                      . Mientras no haya costos reales registrados, la
                      diferencia que ves arriba es "todo lo presupuestado".
                    </span>
                  }
                />
              )}
              {kpis.estimados.count > 0 &&
                kpis.reales.count > 0 &&
                kpis.reales.count < kpis.estimados.count && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                    message={`${kpis.reales.count} de ${kpis.estimados.count} lavados auditados`}
                    description={
                      <span>
                        Algunos presupuestos del mes aún no tienen un costo
                        real anotado. Para completarlos, ir a{" "}
                        <a
                          onClick={() => navigate("/lavanderia")}
                          style={{ cursor: "pointer" }}
                        >
                          /lavanderia → Costo real
                        </a>
                        .
                      </span>
                    }
                  />
                )}
            </>
          ) : (
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Ingresos — métricas adicionales
                </Typography.Title>
                <Row gutter={[12, 12]}>
                  <Col span={12}>
                    <Statistic
                      title="Promedio por lavado"
                      value={kpis?.kind === "af" ? kpis.promedioPorLavado : 0}
                      formatter={(v) => clp(Number(v))}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Propiedades atendidas"
                      value={
                        kpis?.kind === "af" ? kpis.propiedadesAtendidas : 0
                      }
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Lavados con extras"
                      value={kpis?.kind === "af" ? kpis.lavadosConExtras : 0}
                      suffix={
                        resumen.cantidad > 0
                          ? ` / ${resumen.cantidad}`
                          : undefined
                      }
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Monto extras"
                      value={resumen.totalExtraMonto}
                      formatter={(v) => clp(Number(v))}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                </Row>
              </Col>

              <Col xs={24} md={12}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Gastos operacionales — desglose
                </Typography.Title>
                {gastos.length === 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    message="Sin gastos operacionales registrados en este período"
                    description={
                      <span>
                        Para registrarlos, ir a{" "}
                        <a onClick={() => navigate("/lavanderia")} style={{ cursor: "pointer" }}>
                          /lavanderia → Gastos y utilidad
                        </a>
                        .
                      </span>
                    }
                  />
                ) : (
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Statistic
                        title="Servicios (agua/gas/arriendo/luz)"
                        value={gastosDesglose.servicios}
                        formatter={(v) => clp(Number(v))}
                        valueStyle={{ fontSize: 16, color: "#cf1322" }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Sueldos"
                        value={gastosDesglose.sueldos}
                        formatter={(v) => clp(Number(v))}
                        valueStyle={{ fontSize: 16, color: "#cf1322" }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Materiales"
                        value={gastosDesglose.materiales}
                        formatter={(v) => clp(Number(v))}
                        valueStyle={{ fontSize: 16, color: "#cf1322" }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Meses con gastos"
                        value={gastos.length}
                        valueStyle={{ fontSize: 16 }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          )}

          <div
            style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid #e8e8e8",
              textAlign: "center",
            }}
          >
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={verDetalleEnLavanderia}
            >
              Ver registros completos en /lavanderia
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export function LavanderiaSection({
  // `propiedades` y `usuarios` se mantienen en la signature por compat con
  // el padre; no se usan en la vista resumen (los filtros viven en
  // /lavanderia para el detalle).
  propiedades: _propiedades,
  usuarios: _usuarios,
  filtroFecha,
  refreshKey,
}: {
  propiedades: OpcionBasica[];
  usuarios: OpcionBasica[];
  filtroFecha?: FiltroFecha;
  /** Contador de refresco manual (botón "Actualizar" del header). */
  refreshKey?: number;
}) {
  const navigate = useNavigate();

  // Catálogo de lavanderías. Render: una Card por cada lavandería activa.
  const [lavanderias, setLavanderias] = useState<LavanderiaOption[]>([]);
  const [loadingLavanderias, setLoadingLavanderias] = useState(false);

  useEffect(() => {
    setLoadingLavanderias(true);
    finanzasEmpresaApi
      .getLavanderias()
      .then((res) => {
        if (!res?.data) return;
        const opts: LavanderiaOption[] = res.data
          .filter((l: any) => l.activo !== false)
          .map((l: any) => ({
            id: l.id,
            nombre: l.nombre,
            es_generica: !!l.es_generica,
          }));
        // Orden: genérica externa primero (esquemáticamente "el sistema"),
        // luego AF internas alfabéticas. Más fácil de escanear visualmente
        // que un orden por id arbitrario.
        opts.sort((a, b) => {
          if (a.es_generica && !b.es_generica) return -1;
          if (!a.es_generica && b.es_generica) return 1;
          return a.nombre.localeCompare(b.nombre);
        });
        setLavanderias(opts);
      })
      .catch(() => getMsg()?.error("Error al cargar lavanderías"))
      .finally(() => setLoadingLavanderias(false));
  }, [refreshKey]);

  // Helper para detectar si el filtro original era rango (para mostrar
  // aviso de que en finanzas se redujo a mes).
  const isRangoMode =
    filtroFecha?.modo === "rango" && filtroFecha?.rango != null;

  // Etiqueta del rango activo (display).
  const rangoActivo = useMemo(() => {
    const ff = filtroFecha;
    if (!ff || ff.modo === "mes") {
      const m = ff?.mes ?? ahoraChile();
      return m.format("MMMM YYYY");
    }
    if (ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile().subtract(1, "month");
      return `${m.format("MMMM YYYY")} (mes anterior)`;
    }
    if (ff.modo === "rango" && ff.rango) {
      return `${ff.rango[0].format("DD-MM-YYYY")} → ${ff.rango[1].format("DD-MM-YYYY")}`;
    }
    return "Mes actual";
  }, [filtroFecha]);

  return (
    <div className="finanzas-section">
      {/* Indicador del rango activo (heredado del FiltroGlobalFecha del page). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          padding: "8px 12px",
          background: "#f3e8ff",
          borderRadius: 6,
        }}
      >
        <span style={{ fontWeight: 600, color: "#7c3aed" }}>📅 Período:</span>
        <Tag color="purple" style={{ fontSize: 13, textTransform: "capitalize" }}>
          {rangoActivo}
        </Tag>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          (cambia el rango en el selector global de arriba)
        </span>
      </div>

      {/* Banner explicativo general. Round 2026-05-13: rewrite a explicación
          explícita del flujo aseo↔lavandería (cómo se genera el Lavado auto,
          quién paga realmente, y cómo se reconcilia con la pestaña Aseos
          para no doble-contar). */}
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="Cómo funciona la lavandería en las finanzas de AF Empresa"
        description={
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0, marginBottom: 8 }}>
              <b>De dónde viene cada Lavado</b>: cuando se crea un aseo con
              subitem <em>lavandería &gt; 0</em>, el sistema genera
              automáticamente un <b>Lavado</b> vinculado al booking en la
              lavandería asociada a la propiedad. También se pueden crear
              lavados manuales desde <a onClick={() => navigate("/lavanderia")} style={{ cursor: "pointer" }}>/lavanderia</a>.
            </p>

            <p style={{ marginTop: 0, marginBottom: 4 }}>
              <b>🏭 Lavandería externa</b> (genérica del catálogo):
            </p>
            <ul style={{ marginTop: 0, marginBottom: 8, paddingLeft: 20 }}>
              <li>
                El subitem <em>lavandería</em> de cada aseo se registra como un{" "}
                <b>cobro estimado</b> (lo que AF presupuestó pagar a la
                externa). Esto se ve acá como <em>Estimado (presupuesto)</em>.
              </li>
              <li>
                <b>AF paga realmente</b> a la externa el monto que la externa
                cobre — que puede diferir del estimado. El admin lo anota en{" "}
                <em>/lavanderia → Costo real</em> con la fecha, nombre y monto
                cobrado. Eso se ve acá como <em>Costo real (manual)</em>.
              </li>
              <li>
                <b>Diferencia AF = Estimado − Real</b>. <b style={{ color: "#389e0d" }}>Positivo</b>:
                AF presupuestó de más → margen. <b style={{ color: "#cf1322" }}>Negativo</b>:
                la externa cobró más → pérdida real. Esa diferencia
                <b> reconcilia</b> el egreso registrado en la pestaña{" "}
                <em>Aseos</em> (donde figura el estimado como egreso AF cuando
                AF paga el aseo): aseos egresa el estimado, lavandería externa
                aporta ±la diferencia, suma = pérdida real para AF.
              </li>
            </ul>

            <p style={{ marginTop: 0, marginBottom: 4 }}>
              <b>🏢 Lavandería AF interna</b> (sub-entidad con contabilidad propia):
            </p>
            <ul style={{ marginTop: 0, marginBottom: 8, paddingLeft: 20 }}>
              <li>
                El subitem <em>lavandería</em> de cada aseo se registra como{" "}
                <b>ingreso real</b> de la lavandería interna (el valor literal
                que AF "cobra" a sus propios aseos). Acá se ve como{" "}
                <em>Ingresos sub-entidad</em>.
              </li>
              <li>
                La lavandería interna tiene sus propios{" "}
                <b>gastos operativos</b> (agua, gas, arriendo, luz, sueldos,
                materiales) que se anotan en{" "}
                <em>/lavanderia → Gastos op.</em>. Acá se ven como{" "}
                <em>Gastos op. propios</em>.
              </li>
              <li>
                La <b>Utilidad sub-entidad = Ingresos − Gastos op.</b> es la
                rentabilidad real de la lavandería como negocio interno.
              </li>
              <li>
                <b>Consolidado AF</b>: cuando AF paga un aseo y el subitem
                lavandería va a una AF interna, la pestaña Aseos lo registra
                como egreso AF y esta pestaña lo registra como ingreso de la
                interna → consolidado <b>neto 0</b> (es transferencia
                AF→AF interna).
              </li>
            </ul>

            <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
              <b>Regla clave</b>: para el <em>Neto AF Empresa</em> consolidado,
              cruzar siempre las pestañas <em>Aseos</em> y <em>Lavandería</em>{" "}
              — los flujos están espejados a propósito para que cada
              sub-entidad tenga contabilidad propia, pero NO son agregables
              linealmente (las internas se autocancelan; las externas suman
              ±diferencia).
            </p>
          </div>
        }
      />

      {/* Aviso de vista mensual cuando el filtro global es "rango". */}
      {isRangoMode && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`En finanzas la lavandería se muestra siempre por mes completo. Mostrando ${rangoActivo.split("→")[0]?.trim() || "el mes inicial del rango"}. Para granularidad por día, usar /lavanderia.`}
        />
      )}

      {loadingLavanderias ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
          <div style={{ marginTop: 12, color: "#888" }}>
            Cargando catálogo de lavanderías...
          </div>
        </div>
      ) : lavanderias.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Catálogo vacío"
          description="No hay lavanderías activas registradas. Crear al menos una en /lavanderia → 'Nueva lavandería' para ver resúmenes acá."
        />
      ) : (
        lavanderias.map((l) => (
          <LavanderiaCard
            key={l.id}
            lavanderia={l}
            filtroFecha={filtroFecha}
            refreshKey={refreshKey}
          />
        ))
      )}
    </div>
  );
}
